import { useState } from "react";
import {
  makeStyles,
  createStyles,
  RadioGroupProps,
  InputAdornment,
  Typography,
  Theme,
} from "@material-ui/core";
import "date-fns";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";

import { RadioGroup } from "~components/Radio";
import { Calendar } from "~components/Icons";

export type DateFilterType = {
  type: "anytime" | "range";
  from?: Date | null;
  to?: Date | null;
};

export type DateFilterProps = {
  filter?: DateFilterType;
  setFilter?: (filter: { date: DateFilterType }) => void;
} & RadioGroupProps;

class LocalizedUtils extends DateFnsUtils {
  getWeekdays() {
    return super.getWeekdays().map((weekday: string) => weekday.charAt(0));
  }
}

export const DateFilter = ({ setFilter, filter = { type: "anytime" } }: DateFilterProps) => {
  const classes = useStyles();
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = (event.target as HTMLInputElement).value as DateFilterType["type"];
    if (typeof setFilter === "function") {
      setFilter({ date: { ...filter, type: value, from: null, to: null } });
    }
  };

  const handleDateChange = (date: Date | null, key: string) =>
    typeof setFilter === "function" && setFilter({ date: { ...filter, [key]: date } });

  return (
    <MuiPickersUtilsProvider utils={LocalizedUtils}>
      <div className={classes.root}>
        <RadioGroup
          id="date-switch"
          onChange={onChange}
          value={filter.type}
          items={[
            { value: "anytime", label: "Any time" },
            { value: "range", label: "Date range" },
          ]}
        />
        {filter.type === "range" && (
          <div className={classes.textfieldRoot}>
            <div className={classes.box}>
              <Typography className={classes.label}>From</Typography>
              <KeyboardDatePicker
                PopoverProps={{
                  style: { zIndex: 1401 }, // @note 1 index bigger vs tooltip
                }}
                rightArrowButtonProps={{
                  color: "primary",
                  classes: { root: classes.arrow },
                }}
                leftArrowButtonProps={{
                  color: "primary",
                  classes: { root: classes.arrow },
                }}
                InputProps={{
                  classes: {
                    input: classes.input,
                  },
                  disableUnderline: true,
                  readOnly: true,
                }}
                keyboardIcon={
                  <InputAdornment position="end">
                    <Calendar className={classes.calendar} />
                  </InputAdornment>
                }
                KeyboardButtonProps={{
                  classes: {
                    root: classes.noripple,
                  },
                  disableRipple: true,
                  disableFocusRipple: true,
                  disableTouchRipple: true,
                }}
                onChange={(event) => handleDateChange(event, "from")}
                onClose={() => setFromOpen(false)}
                onOpen={() => setFromOpen(true)}
                className={classes.textField}
                initialFocusedDate={null}
                inputVariant="standard"
                value={filter.from}
                format="dd/MM/yy"
                variant="inline"
                open={fromOpen}
                disableToolbar
                autoOk
              />
            </div>
            <div className={classes.box}>
              <Typography className={classes.label}>To</Typography>
              <KeyboardDatePicker
                PopoverProps={{
                  style: { zIndex: 1401 }, // @note 1 index bigger vs tooltip
                }}
                rightArrowButtonProps={{
                  color: "primary",
                  classes: { root: classes.arrow },
                }}
                leftArrowButtonProps={{
                  color: "primary",
                  classes: { root: classes.arrow },
                }}
                InputProps={{
                  classes: {
                    input: classes.input,
                  },
                  disableUnderline: true,
                  readOnly: true,
                }}
                keyboardIcon={
                  <InputAdornment position="end">
                    <Calendar className={classes.calendar} />
                  </InputAdornment>
                }
                KeyboardButtonProps={{
                  classes: {
                    root: classes.noripple,
                  },
                  disableRipple: true,
                  disableFocusRipple: true,
                  disableTouchRipple: true,
                }}
                onChange={(event) => handleDateChange(event, "to")}
                onClose={() => setToOpen(false)}
                onOpen={() => setToOpen(true)}
                className={classes.textField}
                inputVariant="standard"
                value={filter.to}
                format="dd/MM/yy"
                variant="inline"
                disableToolbar
                open={toOpen}
                autoOk
              />
            </div>
          </div>
        )}
      </div>
    </MuiPickersUtilsProvider>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
    },
    textfieldRoot: {
      display: "flex",
      flexDirection: "row",
      paddingTop: theme.spacing(2),
    },
    box: {
      display: "flex",
      flexDirection: "column",
      marginRight: theme.spacing(2.5),
    },
    label: {
      fontSize: 16,
      color: theme.palette.dark.main,
      lineHeight: "normal",
      paddingBottom: theme.spacing(1.25),
    },
    textField: {
      boxShadow: "0 1px 1px 0 rgba(51, 91, 130, 0.12), 0 0 1px 0 rgba(10, 31, 68, 0.1)",
      backgroundColor: "white",
      borderRadius: 4,
      paddingLeft: theme.spacing(1.5),
      paddingRight: theme.spacing(1),
    },
    input: {
      color: theme.palette.dark.main,
      fontSize: 14,
      lineHeight: 1.36,
      letterSpacing: "normal",
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
    noripple: {
      backgroundColor: "transparent!important",
      padding: 0,
    },
    space: { marginBottom: theme.spacing(2), paddingLeft: theme.spacing(1.5) },
    container: {
      padding: 0,
    },
    colorPrimary: {
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    circle: {
      width: 14,
      height: 14,
      borderRadius: 16,
      backgroundColor: theme.palette.primary.main,
    },
    arrow: {
      width: 32,
      height: 32,
      borderRadius: 2,
      boxShadow: " 0 1px 1px 0 rgba(51, 91, 130, 0.12), 0 0 1px 0 rgba(10, 31, 68, 0.1)",
      marginRight: theme.spacing(1),
      marginLeft: theme.spacing(1),
    },
    calendar: {
      color: theme.palette.primary.main,
      fontSize: 16,
    },
  }),
);
